import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { action, extendObservable } from 'mobx'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import { projectApi } from '@atheneum/api'

import Epl from './Epl'
import SegmentActions from './SegmentActions'
import Spinner from '@components/spinner'

const getItemStyle = (draggableStyle, isDragging) => {
  return {
    userSelect: 'none',
    margin: `0 0 15px 0`,
    border: 'none',
    boxShadow: isDragging ? '2px 2px 25px -10px black' : '',
    ...draggableStyle,
    cursor: isDragging ? 'grabbing' : 'grab'
  }
}

const getListStyle = isDraggingOver => ({
  padding: '15px 15px 15px 15px',
  margin: '0 -15px 0 -15px',
  transition: 'background 0.2s ease-out',
  background: isDraggingOver ? 'rgba(0, 149, 220, 0.1)' : 'inherit'
})

const ProjectSegmentContent = observer(
  class ProjectSegmentContent extends Component {
    static propTypes = {
      segment: PropTypes.object,
      epls: PropTypes.object,
      staticData: PropTypes.object,
      loggedEvents: PropTypes.object
    }

    constructor (props) {
      super(props)
      extendObservable(this, {
        showRejectedEpls: false,
        showCanceledEpls: false
      })
    }

    toggleDisplayOfRejectedEpls = action(() => {
      this.showRejectedEpls = !this.showRejectedEpls
    })

    toggleDisplayOfCanceledEpls = action(() => {
      this.showCanceledEpls = !this.showCanceledEpls
    })

    isRejected = epl => {
      const { EPL_STATUSES, EPL_PRELIMINARY_TAGS } = this.props.staticData.CONST
      return (
        epl.epl.eplStatusId === EPL_STATUSES.REJECTED ||
        epl.epl.eplPreliminaryTagId ===
          EPL_PRELIMINARY_TAGS.PRELIMINARY_REJECTED
      )
    }

    isCanceled = epl => {
      const { EPL_STATUSES } = this.props.staticData.CONST
      return epl.epl.eplStatusId === EPL_STATUSES.NOT_INTERVIEWED
    }

    isActive = epl => {
      return !this.isCanceled(epl) && !this.isRejected(epl)
    }

    renderEmpty () {
      return (
        <div className='project-segment__message'>
          There are currently no Experts on this segment
        </div>
      )
    }

    renderToggleRejectedEplsLink = epls => {
      const rejectedEpls = epls.filter(this.isRejected)
      if (!this.showRejectedEpls && rejectedEpls.length) {
        return (
          <div
            onClick={this.toggleDisplayOfRejectedEpls}
            className='project-segment__link'
          >
            Show rejected Experts ({rejectedEpls.length})
          </div>
        )
      }

      if (this.showRejectedEpls && rejectedEpls.length) {
        return (
          <div>
            {rejectedEpls.map(epl => (
              <Epl
                key={epl.id}
                epl={epl}
                loggedEvents={this.props.loggedEvents}
              />
            ))}
            <div
              onClick={this.toggleDisplayOfRejectedEpls}
              className='project-segment__link'
            >
              Hide rejected Experts
            </div>
          </div>
        )
      }

      return null
    }

    renderToggleCanceledEplsLink = epls => {
      const canceledEpls = epls.filter(this.isCanceled)
      if (!this.showCanceledEpls && canceledEpls.length) {
        return (
          <div
            onClick={this.toggleDisplayOfCanceledEpls}
            className='project-segment__link'
          >
            Show cancelled Consultations ({canceledEpls.length})
          </div>
        )
      }

      if (this.showCanceledEpls && canceledEpls.length) {
        return (
          <div>
            {canceledEpls.map(epl => (
              <Epl
                key={epl.id}
                epl={epl}
                loggedEvents={this.props.loggedEvents}
              />
            ))}
            <div
              onClick={this.toggleDisplayOfCanceledEpls}
              className='project-segment__link'
            >
              Hide cancelled Consultations
            </div>
          </div>
        )
      }

      return null
    }

    onDragEnd = action((result, epls) => {
      const isDroppedOutside = !result.destination
      if (isDroppedOutside) {
        return
      }

      const [removed] = epls.splice(result.source.index, 1)
      epls.splice(result.destination.index, 0, removed)

      const segmentId = this.props.segment.id
      const eplIds = epls.map(epl => epl.id)
      projectApi.saveSegmentOrder(segmentId, eplIds)
    })

    renderDraggableEpl = (epl, index) => {
      const loggedEvents = this.props.loggedEvents

      // allow style props here
      /* eslint-disable  react/forbid-dom-props */
      return (
        <Draggable key={epl.id} draggableId={epl.id} index={index}>
          {(provided, snapshot) => {
            const classes = snapshot.isDragging
              ? 'epl__draggable epl__draggable--dragging'
              : 'epl__draggable'
            return (
              <div>
                <div
                  ref={provided.innerRef}
                  style={getItemStyle(
                    provided.draggableProps.style,
                    snapshot.isDragging
                  )}
                  className={classes}
                  {...provided.dragHandleProps}
                >
                  <Epl key={epl.id} epl={epl} loggedEvents={loggedEvents} />
                </div>
                {provided.placeholder}
              </div>
            )
          }}
        </Draggable>
      )
      /* eslint-enable  react/forbid-dom-props */
    }

    renderEpls = epls => {
      const activeEpls = epls.filter(this.isActive)

      // allow style props here
      /* eslint-disable  react/forbid-dom-props */
      return (
        <div className='epl-list'>
          <DragDropContext
            onDragEnd={result => this.onDragEnd(result, activeEpls)}
          >
            <Droppable droppableId='droppable'>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  style={getListStyle(snapshot.isDraggingOver)}
                >
                  {activeEpls.map((epl, index) =>
                    this.renderDraggableEpl(epl, index)
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {this.renderToggleRejectedEplsLink(epls)}
          {this.renderToggleCanceledEplsLink(epls)}
        </div>
      )
      /* eslint-enable  react/forbid-dom-props */
    }

    render () {
      const { segment, epls } = this.props

      if (!segment.expanded) return null

      let content = this.renderEpls(epls)

      const classes = segment.expanded
        ? 'project-segment__content project-segment__content--expanded'
        : 'project-segment__content'

      return <div className={classes}>{content}</div>
    }
  }
)

const Segment = ({
  segment,
  loading,
  epls,
  loggedEvents,
  staticData,
  segmentStore,
  projectStore
}) => {
  if (segment.isEmpty) return null
  if (loading) return <Spinner />

  function orderSegmentDown (id) {
    return segmentStore
      .orderDown(id)
      .then(() => segmentStore.getAll(projectStore.project.id))
  }

  function orderSegmentUp (id) {
    return segmentStore
      .orderUp(id)
      .then(() => segmentStore.getAll(projectStore.project.id))
  }

  return (
    <div className='project-segment'>
      <h2 className='project-segment__title' onClick={segment.toggle}>
        {segment.name || 'Unnamed segment'}
        <SegmentActions
          expanded={segment.expanded}
          orderSegmentDown={() => orderSegmentDown(segment.id)}
          orderSegmentUp={() => orderSegmentUp(segment.id)}
        />
      </h2>
      <ProjectSegmentContent
        segment={segment}
        epls={epls}
        loggedEvents={loggedEvents}
        staticData={staticData}
      />
    </div>
  )
}

Segment.propTypes = {
  segment: PropTypes.object,
  loading: PropTypes.bool,
  epls: PropTypes.object,
  loggedEvents: PropTypes.object,
  staticData: PropTypes.object,
  segmentStore: PropTypes.object,
  projectStore: PropTypes.object
}

export default inject(
  'staticData',
  'segmentStore',
  'projectStore'
)(observer(Segment))
